/**
 * Вкладка «Справочник» — СПИСОК ЗАПИСЕЙ, а не таблица сопоставлений (AK-546).
 *
 * UAT founder 07.08, дословно: «Справочник это справочник… здесь выводить уже
 * РЕЗУЛЬТАТ — то, что мы считаем списком, и то, что можно править. А ты по сути
 * тут выводишь статус настройки метча… Это не справочник, а таблица метча».
 *
 * Он прав, и это ровно перекос Р-4, который фича должна была снять: ч.2 сменила
 * дефолтный фильтр, но оставила МЕТЧЕВЫЕ колонки и счётчики. Здесь экран
 * переписан по wireframe (состояние 1):
 *
 *  · показываем результат — имя, тип, цвет, посевы, видимость, эталон КАК ФАКТ;
 *  · НЕ показываем ничего про сопоставление: ни статусов, ни уверенности, ни
 *    «предложения системы», ни «на разборе платформы»;
 *  · строки, которые ещё НЕ стали записями справочника (`queued` / `suggested`),
 *    сюда не попадают вовсе — они живут в порциях «Интеграций и импорта», и на
 *    них здесь только тихий баннер со ссылкой.
 *
 * Пар, АМП, угодья и смеси — в ОБЩЕМ списке с бейджем типа (решение founder А:
 * «люди воспринимают это как культуру»), внутри системы это по-прежнему
 * раздельные сущности (Р-5).
 */
import { useMemo, useState } from 'react';
import { CircleAlert, GitMerge, Plus, Search } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { SEARCH_PLACEHOLDER, matchesSearch } from '@/lib/reference/normalizeSearch';
import {
  cropVisibilityLabel,
  sowingsCountLabel,
  useArchiveCropRecord,
  useTenantCrops,
  type CropQueueItem,
} from '@/lib/reference/tenantCrops';
import { AddCropFromCanonDialog } from './AddCropFromCanonDialog';
import { CatalogRecordSheet } from './CatalogRecordSheet';
import { CropMergeSheet } from './CropMergeSheet';
import { plural, typeBadgeLabel } from './catalog-review/reviewLabels';

/**
 * Тип записи в общем списке (решение А) — по нему же фильтр-чипы.
 *
 * `land_parcel` — НЕ часть общего списка (решение founder 08.08, см.
 * `isLandParcel`), это отдельный ОПТ-ИН фильтр: без него запись, помеченную
 * угодьем, было бы нечем найти и нечем вернуть — необратимое действие без
 * кнопки «назад» (требование обратимости, UAT v1.1746).
 */
type RowType = 'crop' | 'crop_mix' | 'fallow' | 'land_parcel';

function rowType(item: CropQueueItem): RowType {
  if (item.not_a_crop_kind === 'land_parcel') return 'land_parcel';
  if (item.not_a_crop_kind === 'crop_mix' || (item.components_count ?? 0) > 0) return 'crop_mix';
  if (item.not_a_crop_kind === 'field_state') return 'fallow';
  return 'crop';
}

const TYPE_LABEL: Record<RowType, string> = {
  crop: 'Культуры',
  crop_mix: 'Смеси',
  fallow: 'Пар и АМП',
  land_parcel: 'Убраны в угодья',
};

/**
 * Категория участка — НЕ занятие поля (решение founder 08.08).
 *
 * Дословно про строки вроде «Передано в аренду»: «это вообще не про культуры…
 * в справочник занятий такие записи НЕ показывать вообще». И это ровно то, что
 * говорит модель: `land_parcel` — свойство УЧАСТКА (`reference.land_category`),
 * а не то, чем занят сезон; сезонное занятие без культуры — это `field_state`
 * (пар/АМП), оно в списке остаётся.
 *
 * Сырые написания из выгрузки никуда не деваются — они остаются материалом
 * сверки в «Интеграциях и импорте». Их судьба — модель земельных активов, а
 * не справочник занятий.
 */
function isLandParcel(item: CropQueueItem): boolean {
  return item.not_a_crop_kind === 'land_parcel';
}

/**
 * Запись справочника — это то, что УЖЕ разобрано и чем можно пользоваться.
 *
 * `matched` — культура с эталоном; `not_a_crop` — пар/АМП, угодье или смесь,
 * то есть тоже полноценное «занятие поля» (Р-5). А `queued` / `suggested` —
 * это ещё НЕ записи: платформа их либо не поняла, либо предложила, и человек
 * решения не принял. Показывать их в справочнике значит показывать статус
 * настройки вместо результата — то, за что founder и развернул экран.
 */
function isCatalogRecord(item: CropQueueItem): boolean {
  if (isLandParcel(item)) return false;
  return item.match_status === 'matched' || item.match_status === 'not_a_crop';
}

/**
 * Записи, УБРАННЫЕ из справочника занятий пометкой «тип угодья».
 *
 * В общем списке их нет и быть не должно, но найти их надо: человек мог
 * промахнуться плиткой, и тогда единственный путь назад — открыть карточку и
 * снять пометку. Поэтому отдельный чип, а не возврат в «Все».
 */
function isRemovedToLand(item: CropQueueItem): boolean {
  return isLandParcel(item) && item.match_status === 'not_a_crop';
}

/**
 * Чем запись является в системе — ФАКТ, а не статус сопоставления.
 *
 * Для культуры это её системный эталон, для пара/угодья — соответствующая
 * запись справочника состояний/угодий, для смеси — «своя запись». Никакой
 * уверенности в процентах и никаких «нужно подтвердить»: то, что запись
 * попала в этот список, уже означает, что вопрос закрыт.
 */
function canonLabel(item: CropQueueItem): string {
  if (item.field_state_name) return item.field_state_name;
  if (item.land_category_name) return item.land_category_name;
  if (item.canonical_name) return item.canonical_name;
  return 'своя запись';
}

/**
 * Под каким именем запись живёт в справочнике.
 *
 * UAT founder 08.08: в списке стояли «Не обрабатывается», «Передано в аренду»,
 * «Распашка пастбищ» — это СЫРЫЕ строки выгрузки АгроХистори, а не записи
 * справочника: «Это артефакты разбора АгроХистори». Для пара/АМП и угодий
 * своего названия у хозяйства не бывает — есть системное состояние поля
 * («Залежь», «Пастбище», «Пар чёрный»), на которое сырые строки и легли.
 * Поэтому такие записи показываем ПОД СИСТЕМНЫМ ИМЕНЕМ, а сколько написаний
 * из источника на него легло — видно в «Интеграциях и импорте», где сверка и
 * живёт (Р-3/Р-4). У культур наоборот: своё название легально («ОзПш»), его и
 * показываем.
 */
function displayName(item: CropQueueItem): string {
  if (item.field_state_name) return item.field_state_name;
  return item.local_name ?? '—';
}

/**
 * Ключ схлопывания: пар/АМП и угодья — одна запись справочника на системное
 * состояние, сколько бы написаний из источника на него ни легло. Культуры и
 * смеси не схлопываем: два разных названия могут быть двумя честными записями
 * (дубли лечатся слиянием, С-6, а не молчаливым склеиванием в списке).
 */
function collapseKey(item: CropQueueItem): string {
  if (item.field_state_code) return `fs:${item.field_state_code}`;
  return `id:${item.id}`;
}

/** Схлопнутые строки + сколько написаний источника стоит за каждой. */
function collapse(items: CropQueueItem[]): Array<{ item: CropQueueItem; aliases: number }> {
  const byKey = new Map<string, { item: CropQueueItem; aliases: number }>();
  for (const it of items) {
    const key = collapseKey(it);
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { item: it, aliases: 1 });
      continue;
    }
    // Выживает запись с наибольшим числом посевов — «обычно она и правильная»
    // (та же логика, что у слияния дублей в макете).
    byKey.set(key, {
      item: it.field_count > prev.item.field_count ? it : prev.item,
      aliases: prev.aliases + 1,
    });
  }
  return [...byKey.values()];
}

export function TenantCatalogListPage() {
  const [type, setType] = useState<RowType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  // С-5: архив — это ФИЛЬТР над тем же списком, а не отдельная вкладка.
  const [archived, setArchived] = useState(false);
  const [openRecord, setOpenRecord] = useState<CropQueueItem | null>(null);
  // С-6: мультивыбор для слияния дублей. Чекбоксы не висят во всех строках
  // постоянно — это шум ради разовой операции; они появляются на наведении и
  // остаются, пока строка выбрана (паттерн AK-564 у тех.операций).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const archiveRecord = useArchiveCropRecord();

  // Тянем весь список и фильтруем на клиенте: тенант-справочник это десятки
  // строк, а серверный фильтр здесь только по МЕТЧ-статусу — то, чего на этом
  // экране быть не должно.
  const query = useTenantCrops('all', null, archived);
  const all = query.data?.items ?? [];

  const records = useMemo(
    () =>
      collapse(
        all.filter(isCatalogRecord).filter((r) => Boolean(r.archived_at) === archived),
      ),
    [all, archived],
  );
  // Убранные в угодья — своя коллекция: в «Все» они не входят (иначе решение
  // founder 08.08 отменено), но по своему чипу должны находиться и открываться.
  const landRecords = useMemo(
    () =>
      collapse(
        all.filter(isRemovedToLand).filter((r) => Boolean(r.archived_at) === archived),
      ),
    [all, archived],
  );
  // «Ждут разбора» — это строки, по которым решение ещё не принято. Категории
  // участка сюда не приплюсовываем: по ним решение принято, просто живут они
  // не в справочнике занятий.
  const pendingReview = all.filter(
    (r) => !isCatalogRecord(r) && !isLandParcel(r),
  ).length;

  const counts = useMemo(() => {
    const acc: Record<RowType, number> = { crop: 0, crop_mix: 0, fallow: 0, land_parcel: 0 };
    for (const r of records) acc[rowType(r.item)] += 1;
    acc.land_parcel = landRecords.length;
    return acc;
  }, [records, landRecords]);

  const rows = useMemo(() => {
    // Чип «Убраны в угодья» показывает СВОЮ коллекцию: этих записей в общем
    // списке нет по определению, поэтому фильтровать `records` бесполезно —
    // получился бы вечно пустой экран вместо пути к отмене.
    const source = type === 'land_parcel' ? landRecords : records;
    return source
      .filter((r) => (type === 'all' || type === 'land_parcel' ? true : rowType(r.item) === type))
      .filter((r) => matchesSearch(displayName(r.item), search))
      .sort((a, b) => displayName(a.item).localeCompare(displayName(b.item), 'ru'));
  }, [records, landRecords, type, search]);

  // Сливать можно только активные записи: у архивной ссылки уже перепривязаны
  // (или её саму только что схлопнули), и «слить в архив» смысла не имеет.
  // Считаем от ВИДИМЫХ строк: иначе человек отмечает две записи, набирает в
  // поиске другое — галочек на экране нет, а панель по-прежнему предлагает
  // «Слить выбранные (2)» и сливает то, чего он не видит.
  const selectedRows = useMemo(
    () =>
      rows
        .map((r) => r.item)
        .filter(
          (i) =>
            selectedIds.has(i.id) &&
            !i.archived_at &&
            rowType(i) !== 'fallow' &&
            // Убранные в угодья не сливаем: у них общий пустой эталон, гард
            // разной привязки их не ловит, а слияние необратимо.
            rowType(i) !== 'land_parcel',
        ),
    [rows, selectedIds],
  );

  function toggleSelected(id: string, on: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Культуры хозяйства</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Список того, чем бывает занято поле: культуры, смеси, пар и АМП, угодья —
            одним списком. Именно его видит агроном в выпадашке при заведении посева.
          </p>
        </div>
        <Button size="sm" className="shrink-0" onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить культуру
        </Button>
      </div>

      {/* Убранные в угодья — объясняем, ГДЕ человек находится и что тут можно
          сделать: без этой строки чип выглядит вторым списком культур. */}
      {type === 'land_parcel' && (
        <p className="text-sm text-muted-foreground">
          Эти записи убраны из справочника занятий: они помечены типом угодья и
          в общий список не входят. Откройте запись, чтобы вернуть её в культуры.
        </p>
      )}

      {/* Тихая ссылка на разбор — ОДНА строка, без счётчиков метча.
          Неразобранное не мешает работать со списком (П-2). */}
      {pendingReview > 0 && (
        <p className="text-sm text-muted-foreground">
          Ещё {pendingReview} названий ждут разбора — они на вкладке «Интеграции и
          импорт» и в список пока не входят.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            { key: 'all' as const, label: 'Все', n: records.length },
            { key: 'crop' as const, label: TYPE_LABEL.crop, n: counts.crop },
            { key: 'crop_mix' as const, label: TYPE_LABEL.crop_mix, n: counts.crop_mix },
            { key: 'fallow' as const, label: TYPE_LABEL.fallow, n: counts.fallow },
            {
              key: 'land_parcel' as const,
              label: TYPE_LABEL.land_parcel,
              n: counts.land_parcel,
            },
          ] as const
        )
          .filter((c) => c.key === 'all' || c.n > 0)
          .map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={type === c.key}
              onClick={() => setType(c.key)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                type === c.key
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground hover:bg-accent',
              )}
            >
              {c.label} · {c.n}
            </button>
          ))}
        {/* С-5: архив — включённый фильтр над тем же списком. */}
        <button
          type="button"
          aria-pressed={archived}
          onClick={() => setArchived((v) => !v)}
          className={cn(
            'rounded-full border px-3 py-1 text-sm transition-colors',
            archived
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-card text-muted-foreground hover:bg-accent',
          )}
        >
          Архив
        </button>
        <div className="relative ml-auto min-w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder={SEARCH_PLACEHOLDER}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : query.isError ? (
        <Alert variant="destructive">
          <CircleAlert className="h-4 w-4" />
          <AlertTitle>Не удалось загрузить справочник</AlertTitle>
          <AlertDescription>
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              Повторить
            </Button>
          </AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm font-medium text-foreground">
            {records.length === 0
              ? archived
                ? 'В архиве пусто'
                : 'Справочник пока пуст'
              : 'Ничего не нашли'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {records.length === 0
              ? 'Добавьте культуру вручную или разберите то, что пришло, во вкладке «Интеграции и импорт».'
              : 'Попробуйте другое написание или снимите фильтр по типу.'}
          </p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                {!archived && <TableHead className="w-10" />}
                <TableHead className="w-[32%]">Название</TableHead>
                <TableHead className="w-[14%]">Тип</TableHead>
                <TableHead className="w-[22%]">В системе это</TableHead>
                <TableHead className="w-[18%]">Видна</TableHead>
                <TableHead className="w-[14%]">Посевов</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ item }) => (
                <TableRow
                  key={item.id}
                  tabIndex={0}
                  role="button"
                  aria-label={`Открыть «${displayName(item)}»`}
                  className="group cursor-pointer"
                  onClick={() => setOpenRecord(item)}
                  onKeyDown={(e) => {
                    // Событие с чекбокса (Radix рисует его кнопкой) всплывает
                    // сюда, и preventDefault глотал бы Space НА ЧЕКБОКСЕ:
                    // мышью выбрать можно, с клавиатуры — нет.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setOpenRecord(item);
                    }
                  }}
                >
                  {!archived && (
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {/* Пар, АМП и угодья слиянием НЕ схлопываем: «Пар чёрный»
                          и «Залежь» — два разных состояния поля, и гард разной
                          привязки их не ловит (у обеих эталон пуст). Список и
                          так держит по одной строке на состояние — сливать там
                          нечего, а необратимое действие предлагать нельзя. */}
                      {rowType(item) !== 'fallow' && rowType(item) !== 'land_parcel' && (
                      <Checkbox
                        className={cn(
                          'transition-opacity',
                          selectedIds.has(item.id)
                            ? 'opacity-100'
                            : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
                        )}
                        checked={selectedIds.has(item.id)}
                        onCheckedChange={(v) => toggleSelected(item.id, Boolean(v))}
                        aria-label={`Выбрать «${displayName(item)}» для слияния`}
                      />
                      )}
                    </TableCell>
                  )}
                  <TableCell className="font-medium text-foreground">
                    <span className="flex items-center gap-2">
                      {/* Цвет — тот, которым культура рисуется на карте. */}
                      <span
                        aria-hidden
                        className="inline-block h-3 w-3 shrink-0 rounded-sm border border-border"
                        style={item.crop_color ? { backgroundColor: item.crop_color } : undefined}
                      />
                      <span className="min-w-0 truncate">{displayName(item)}</span>
                    </span>
                    {item.growing_goal_name_ru && (
                      <Badge variant="secondary" className="mt-1">
                        {item.growing_goal_name_ru}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {typeBadgeLabel(
                        item.not_a_crop_kind ?? null,
                        (item.components_count ?? 0) > 0,
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {canonLabel(item)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {cropVisibilityLabel(item)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {sowingsCountLabel(item.field_count)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Нижняя панель выбора — вход в слияние. Кнопки «Слить» в шапке нет:
          пока никого не выбрали, ей там нечего делать. */}
      {selectedRows.length >= 2 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card p-4">
          <span className="text-sm text-muted-foreground">
            Выбрано{' '}
            <strong className="text-foreground">{selectedRows.length}</strong>{' '}
            {plural(selectedRows.length, 'запись', 'записи', 'записей')} — их можно
            слить в одну.
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
              Снять выбор
            </Button>
            <Button size="sm" onClick={() => setMergeOpen(true)}>
              <GitMerge className="mr-2 h-4 w-4" />
              Слить выбранные ({selectedRows.length})
            </Button>
          </div>
        </div>
      )}

      <AddCropFromCanonDialog open={addOpen} onOpenChange={setAddOpen} />

      <CatalogRecordSheet
        item={openRecord}
        open={openRecord !== null}
        onOpenChange={(o) => !o && setOpenRecord(null)}
        /* Рабочий выход из гарда перепривязки (обратный перенос из #728): старая
           запись в архив, форма сразу открывается на создание новой — вместо
           «заархивируйте и добавьте новую» без единой кнопки. */
        onArchiveAndCreate={(rec) =>
          archiveRecord.mutate(rec.id, {
            onSuccess: () => {
              toast.success('Запись в архиве — заведите новую');
              setOpenRecord(null);
              setAddOpen(true);
            },
            onError: () => toast.error('Не удалось заархивировать'),
          })
        }
      />

      <CropMergeSheet
        open={mergeOpen}
        selected={selectedRows}
        onClose={() => setMergeOpen(false)}
        onMerged={() => setSelectedIds(new Set())}
      />
    </div>
  );
}

export default TenantCatalogListPage;
